import type { ReactNode } from "react";
import styles from "./Avatar.module.css";

type AvatarSize = 24 | 28 | 32 | 44;
type AvatarShape = "square" | "circle";

interface AvatarProps {
  initials?: string;
  src?: string;
  icon?: ReactNode;
  size?: AvatarSize;
  shape?: AvatarShape;
  ariaLabel?: string;
}

const FONT_SIZE_BY_SIZE: Record<AvatarSize, number> = {
  24: 10,
  28: 11,
  32: 13,
  44: 15,
};

/**
 * Avatar — `--surface-well` filled block, square by default with
 * `--radius-chip`, circle when explicit. Renders src if provided,
 * else icon, else initials, else nothing (empty tile). The order
 * is deliberate: an image beats a placeholder, always.
 */
export function Avatar({
  initials,
  src,
  icon,
  size = 32,
  shape = "square",
  ariaLabel,
}: AvatarProps) {
  const shapeClass = shape === "circle" ? styles.circle : styles.square;
  return (
    <div
      className={`${styles.avatar} ${shapeClass}`}
      style={{
        width: size,
        height: size,
        fontSize: FONT_SIZE_BY_SIZE[size],
      }}
      role={ariaLabel ? "img" : undefined}
      aria-label={ariaLabel}
    >
      {src ? (
        <img className={styles.img} src={src} alt={ariaLabel ?? ""} />
      ) : icon ? (
        <span aria-hidden>{icon}</span>
      ) : initials ? (
        <span aria-hidden>{initials}</span>
      ) : null}
    </div>
  );
}
